// /store/animeDB.slice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { toast } from 'react-hot-toast'
import {
  loadAnimes,
  loadAnimeById,
  saveAnime,
  removeAnime,
  loadAnimesByIds,
} from '../../controllers/animeDB.controller.js'

/** --- Async Actions (Thunks) --- **/

export const fetchAnimes = createAsyncThunk(
  'animeDB/fetchAnimes',
  async (filterBy, { rejectWithValue }) => {
    try {
      return await loadAnimes(filterBy)
    } catch (err) {
      toast.error('שגיאה בטעינת האנימות')
      return rejectWithValue(err)
    }
  }
)

export const fetchMoreAnimes = createAsyncThunk(
  'animeDB/fetchMoreAnimes',
  async (filterBy, { getState, rejectWithValue }) => {
    try {
      const currentIds = getState().animeDB.animes.map((a) => a.mal_id)
      const newAnimes = await loadAnimes(filterBy)
      return newAnimes.filter((anime) => !currentIds.includes(anime.mal_id))
    } catch (err) {
      toast.error('שגיאה בטעינת אנימות נוספות')
      return rejectWithValue(err)
    }
  }
)

export const fetchAnimeById = createAsyncThunk(
  'animeDB/fetchAnimeById',
  async (id, { rejectWithValue }) => {
    try {
      return await loadAnimeById(id)
    } catch (err) {
      toast.error(`שגיאה בטעינת אנימה ID=${id}`)
      return rejectWithValue(err)
    }
  }
)

export const fetchAnimesByIds = createAsyncThunk(
  'animeDB/fetchAnimesByIds',
  async (ids, { rejectWithValue }) => {
    try {
      return await loadAnimesByIds(ids)
    } catch (err) {
      toast.error('שגיאה בטעינת אנימות לפי מזהים')
      return rejectWithValue(err)
    }
  }
)

export const saveAnimeThunk = createAsyncThunk(
  'animeDB/saveAnimeThunk',
  async (anime, { rejectWithValue }) => {
    try {
      const saved = await saveAnime(anime)
      toast.success('אנימה נשמרה בהצלחה!')
      return saved
    } catch (err) {
      toast.error('שגיאה בשמירת אנימה')
      return rejectWithValue(err)
    }
  }
)

export const removeAnimeThunk = createAsyncThunk(
  'animeDB/removeAnimeThunk',
  async (id, { rejectWithValue }) => {
    try {
      await removeAnime(id)
      toast.success('אנימה נמחקה בהצלחה!')
      return id
    } catch (err) {
      toast.error(`שגיאה במחיקת אנימה ID=${id}`)
      return rejectWithValue(err)
    }
  }
)

/** --- Initial State --- **/

const initialState = {
  animes: [],
  selectedAnime: null,
  relationsData: {},
  isLoading: false,
  isLoadingMore: false,
  filterBy: {
    title: '',
    title_english: '',
    title_hebrew: '',
    year: '',
    genres: [],
    themes: [],
    status: '',
    demographics: [],
    top100: false,
    score: '',
    sort: '',
    excludeMatureContent: true,
    page: 1,
    limit: 30,
  },
}

/** --- Slice --- **/

const animeDBSlice = createSlice({
  name: 'animeDB',
  initialState,
  reducers: {
    setFilter(state, action) {
      state.filterBy = { ...state.filterBy, ...action.payload }
    },
    clearSelectedAnime(state) {
      state.selectedAnime = null
    },
    resetFilter(state) {
      state.filterBy = initialState.filterBy
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchAnimes
      .addCase(fetchAnimes.pending, (state) => {
        state.isLoading = true
      })
      .addCase(fetchAnimes.fulfilled, (state, action) => {
        state.isLoading = false
        state.animes = action.payload || []
      })
      .addCase(fetchAnimes.rejected, (state) => {
        state.isLoading = false
      })

      // fetchMoreAnimes
      .addCase(fetchMoreAnimes.pending, (state) => {
        state.isLoadingMore = true
      })
      .addCase(fetchMoreAnimes.fulfilled, (state, action) => {
        state.isLoadingMore = false
        state.animes = [...state.animes, ...(action.payload || [])]
      })
      .addCase(fetchMoreAnimes.rejected, (state) => {
        state.isLoadingMore = false
      })

      // fetchAnimeById
      .addCase(fetchAnimeById.fulfilled, (state, action) => {
        const anime = action.payload
        if (anime) {
          state.selectedAnime = anime
          state.relationsData[anime.mal_id] = anime
        }
      })

      // fetchAnimesByIds
      .addCase(fetchAnimesByIds.fulfilled, (state, action) => {
        action.payload.forEach((anime) => {
          state.relationsData[anime.mal_id] = anime
        })
      })

      // removeAnimeThunk
      .addCase(removeAnimeThunk.fulfilled, (state, action) => {
        const malId = action.payload
        state.animes = state.animes.filter((anime) => anime.mal_id !== malId)
        delete state.relationsData[malId]
      })
  },
})

/** --- Exports --- **/

export const { setFilter, clearSelectedAnime, resetFilter } =
  animeDBSlice.actions
export const animeDBReducer = animeDBSlice.reducer
